<template>
  <Dialog
      :header="t('REGISTER_NEW_USER')"
      v-model:visible="visible"
      modal
      :style="{ width: '32rem' }"
      :draggable="false"
      :dismissable-mask="true"
      @hide="onHide"
  >
    <div>
      <form @submit.prevent="addAccount">
        <div class="mb-2">
          <label for="add-account-ssn" class="form-label">{{ t('SSN') }}</label>
          <InputText
              id="add-account-ssn"
              v-model="formData.ssn"
              type="text"
              class="w-100"
              :invalid="submitted && !!ssnInvalidFeedback"
              required
          />
          <small v-if="submitted && !!ssnInvalidFeedback" class="text-danger">{{ ssnInvalidFeedback }}</small>
        </div>

        <div class="mb-2">
          <label for="add-account-role" class="form-label">{{ t('ROLE') }}</label>
          <Select
              label-id="add-account-role"
              v-model="formData.roleType"
              :options="roleOptions"
              option-label="label"
              option-value="value"
              :placeholder="t('SELECT')"
              class="w-100"
              :invalid="submitted && !!roleInvalidFeedback"
              :disabled="isRoleSelectionDisabled"
          />
          <small v-if="submitted && !!roleInvalidFeedback" class="text-danger">{{ roleInvalidFeedback }}</small>
        </div>
      </form>
    </div>

    <template #footer>
      <Button :label="t('CANCEL')" severity="secondary" @click="visible = false"/>
      <Button :label="t('SAVE')" @click="addAccount"/>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import {useI18n} from "vue-i18n";
import {Role} from "@/auth/oauth";
import type {ErrorResponse} from "@/error/errorResponse";
import {getViolationMessage} from "@/error/errorResponse";
import {validateSsn} from "@/util/estId";
import {injectAuth, injectAxios} from "@/util/inject";

interface AddAccountFormData {
  ssn?: string;
  roleType?: string;
}

const axios = injectAxios();
const auth = injectAuth();
const {t} = useI18n();

const formData = ref<AddAccountFormData>({})
const errorResp = ref<ErrorResponse<AddAccountFormData>>({});
const submitted = ref(false);
const visible = ref(false);

const props = defineProps<{
  id: string;
  afterSave?: () => Promise<any>;
}>();

const roleInvalidFeedback = computed(() => {
  const violationMsg = getViolationMessage("roleType", errorResp.value);
  if (violationMsg) {
    return t(violationMsg);
  }

  if (!formData.value.roleType) {
    return t("ERRORS.FIELD_IS_REQUIRED");
  }

  return "";
});

const ssnInvalidFeedback = computed(() => {
  const violationMsg = getViolationMessage("ssn", errorResp.value);
  if (violationMsg) {
    return t(violationMsg);
  }

  if (!formData.value.ssn) {
    return t("ERRORS.FIELD_IS_REQUIRED");
  }

  if (!validateSsn(formData.value.ssn)) {
    return t("FAULTY_SSN");
  }

  return "";
});

const isRoleSelectionDisabled = computed(() => auth.isRvktAdmin() && !auth.hasRole(Role.ADMIN));

const roleOptions = computed(() => {
  const roles = Object.keys(Role)
      .map(role => Role[role])
      .filter(role => role != Role.ALL && role != Role.DISABLED_USER);

  const allowedRoles = auth.isRvktAdmin() && !auth.hasRole(Role.ADMIN)
      ? [Role.TRAINEE]
      : roles;

  return allowedRoles.map(role => ({label: t(role), value: role}));
});

const onHide = () => {
  visible.value = false;
};

const openModal = () => {
  submitted.value = false;
  formData.value = isRoleSelectionDisabled.value ? {roleType: Role.TRAINEE} : {};
  visible.value = true;
};

const addAccount = async (ev: Event) => {
  ev.preventDefault();
  submitted.value = true;

  if (!formData.value.ssn || !formData.value.roleType || !validateSsn(formData.value.ssn)) {
    return;
  }

  const requestData = {
    ssn: formData.value.ssn,
    roleType: formData.value.roleType,
  };

  try {
    await axios.post(`/account/by-admin`, requestData);
  } catch (e: any) {
    errorResp.value = e.response?.data;
    throw e;
  }

  if (props.afterSave) {
    await props.afterSave();
  }

  visible.value = false;
};

defineExpose({
  openModal,
});
</script>
